<template>
  <div class="add-package-modal-body">
    <v-toolbar class="modal-toolbar" density="comfortable" flat>
      <div class="modal-bar">
        <v-btn icon variant="text" class="back-btn" aria-label="Back" @click="modal.close()">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div class="modal-title">Add package</div>
      </div>
    </v-toolbar>

    <v-card-text class="body py-6">
      <v-form class="form" @submit.prevent="handleSubmit">
        <AppTextField
          v-model="name"
          label="Package name"
          placeholder="e.g., Basic Pest Control"
          :error-messages="nameError"
          @update:modelValue="nameError = ''"
        />
        <AppTextArea
          v-model="highlight"
          label="Highlight"
          placeholder="e.g., Exterior treatment + monthly follow-up"
          rows="3"
          :error-messages="highlightError"
          @update:modelValue="highlightError = ''"
        />
        <AppTextArea
          v-model="features"
          label="Features"
          placeholder="e.g., Spray, Inspection, Warranty"
          hint="Enter features separated by commas.."
          rows="4"
          :error-messages="featuresError"
          @update:modelValue="featuresError = ''"
        />

        <div class="section-title">
          <AppFieldLabel label="Pricing Tiers" />
        </div>
        <div class="pricing-list">
          <div
            v-for="(price, index) in pricing"
            :key="index"
            class="pricing-item"
          >
          
            <div class="pricing-row">
              <div class="pricing-field">
                <AppSelectField
                  v-model="price.type"
                  :items="priceTypeOptions"
                  :clearable="false"
                  hide-details
                  @update:modelValue="() => pricingError = ''"
                />
              </div>
              <div class="pricing-field">
                <AppTextField
                  v-model="price.amount"
                  type="number"
                  placeholder="0.00"
                  prefix="$"
                  hide-details
                  @update:modelValue="() => pricingError = ''"
                />
              </div>
              <div>
                 <v-btn
                v-if="pricing.length > 1"
                size="small"
                variant="text"
                icon="mdi-delete"
                color="error"
                @click="removePricing(index)"
                class="delete-btn"
              />
              </div>
            </div>
          </div>
          <AppButtonFlat
            variant="outlined"
            prepend-icon="mdi-plus"
            @click="addPricing"
            class="add-pricing-btn"
          >
            Add another pricing tier
          </AppButtonFlat>

          <div v-if="pricingError" class="api-error">{{ pricingError }}</div>
        </div>

        <div class="actions">
          <AppButtonFlat block @click="handleSubmit" prepend-icon="mdi-check" color="primary">
            Create
          </AppButtonFlat>
        </div>
      </v-form>
    </v-card-text>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import AppTextArea from "@/shared/components/ui/AppTextArea.vue";
import { useModalStore } from "@/shared/stores/modal";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import AppSelectField from "@/shared/components/ui/AppSelectField.vue";
import AppFieldLabel from "@/shared/components/ui/AppFieldLabel.vue";

type ServiceOption = {
  id: string;
  title: string;
  subtitle: string;
};

type PackagePricing = {
  type: string;
  amount: number | string;
};

type Package = {
  name: string;
  highlight: string;
  features: string;
  pricing: PackagePricing[];
};

const props = defineProps<{
  onAdd?: (pkg: Package) => void;
}>();

const modal = useModalStore();

const name = ref("");
const highlight = ref("");
const features = ref("");
const selectedServiceIds = ref<string[]>([]);
const pricing = ref<PackagePricing[]>([{ type: "month", amount: "" }]);

// Error messages for validation
const nameError = ref("");
const highlightError = ref("");
const featuresError = ref("");
const pricingError = ref("");

const isEditing = computed(() => false);

const availableServices: ServiceOption[] = [
  { id: "pest_control", title: "Pest Control", subtitle: "General pest control, inspections, and treatments." },
  { id: "termite", title: "Termite", subtitle: "Termite inspection, prevention, and remediation." },
  { id: "rodent", title: "Rodent", subtitle: "Rodent control, exclusion, and cleanup recommendations." },
  { id: "bed_bugs", title: "Bed Bugs", subtitle: "Assessment and targeted treatment plans." },
  { id: "mosquito", title: "Mosquito", subtitle: "Yard treatments and seasonal maintenance." },
];

const priceTypeOptions = [
  { title: "Daily", value: "day" },
  { title: "Weekly", value: "week" },
  { title: "Monthly", value: "month" },
  { title: "Yearly", value: "year" },
];

// Initialize form
// No editing functionality needed for create-only modal

function slugify(input: string) {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function generateId() {
  const base = slugify(name.value) || "package";
  return `${base}_${Date.now()}`;
}

function toggleService(serviceId: string, checked: boolean) {
  const next = new Set(selectedServiceIds.value);
  if (checked) next.add(serviceId);
  else next.delete(serviceId);
  selectedServiceIds.value = Array.from(next);
}

function addPricing() {
  pricing.value.push({ type: "month", amount: "" });
}

function removePricing(index: number) {
  if (pricing.value.length > 1) {
    pricing.value.splice(index, 1);
  }
}

function handleSubmit() {
  // Clear previous errors
  nameError.value = "";
  highlightError.value = "";
  featuresError.value = "";
  pricingError.value = "";

  const pkgName = name.value.trim();
  if (!pkgName) {
    nameError.value = "Package name is required";
    return;
  }

  const pkgHighlight = highlight.value.trim();
  if (!pkgHighlight) {
    highlightError.value = "Package highlight is required";
    return;
  }

  const pkgFeatures = features.value.trim();
  if (!pkgFeatures) {
    featuresError.value = "Package features are required";
    return;
  }

  // Validate pricing tiers: ensure each has a type and numeric amount > 0
  const pricingMsgs: string[] = [];
  const validatedPricing = [] as PackagePricing[];
  pricing.value.forEach((p, idx) => {
    const row = idx + 1;
    // normalize type value: if item object provided, use its value
    const typeVal = (p as any)?.type ?? p.type;
    if (!typeVal) {
      pricingMsgs.push(`Pricing tier ${row}: type is required.`);
    }
    const amtRaw = p.amount === "" ? null : Number(p.amount);
    if (amtRaw === null || Number.isNaN(amtRaw) || amtRaw <= 0) {
      pricingMsgs.push(`Pricing tier ${row}: amount is required and must be greater than 0.`);
    }
    if (typeVal && amtRaw !== null && !Number.isNaN(amtRaw) && amtRaw > 0) {
      validatedPricing.push({ type: typeVal, amount: amtRaw });
    }
  });

  if (pricingMsgs.length > 0) {
    pricingError.value = pricingMsgs.join(' ');
    return;
  }

  const pkg: Package = {
    name: pkgName,
    highlight: pkgHighlight,
    features: pkgFeatures,
    pricing: validatedPricing
  };

  props.onAdd?.(pkg);
  
  modal.close();
}
</script>

<style scoped>
.add-package-modal-body {
  padding: 0 0 calc(env(safe-area-inset-bottom, 0px) + 16px);
  min-height: 100dvh;
}

.modal-toolbar {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--color-theme);
  padding: calc(env(safe-area-inset-top, 0px) + 8px) 0 0;
  height: auto;
}

.modal-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 0;
}

:deep(.modal-toolbar .v-toolbar__content) {
  height: auto;
  min-height: 64px;
  align-items: center;
  padding: 8px 16px;
}

.back-btn {
  width: 50px;
  height: 50px;
  border-radius: var(--radius-lg);
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.body {
  padding-top: 12px;
}

.form {
  display: grid;
  gap: 16px;
}

.section-title {
  margin-top: 8px;
  font-weight: 600;
  color: #374151;
}

.services-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.service-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-background);
  cursor: pointer;
}

.service-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
}

.service-title {
  font-weight: 600;
  color: #111827;
  font-size: 14px;
}

.service-subtitle {
  color: #6b7280;
  font-size: 12px;
  line-height: 1.3;
}

.service-check {
  margin: 0;
}

.pricing-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.pricing-item {
  padding: 8px 0;
}

.pricing-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.pricing-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.delete-btn {
  opacity: 0.7;
  transition: opacity 0.2s;
}

.delete-btn:hover {
  opacity: 1;
}

.pricing-row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.pricing-field {
  flex: 1;
}

.add-pricing-btn {
  margin-top: 8px;
}

.pricing-row {
  display: flex;
  gap: 12px;
  align-items: center;
}

.pricing-row .v-select {
  flex: 1;
}

.pricing-row .app-text-field {
  flex: 1;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
}

.api-error {
  color: #ef4444;
  font-size: 14px;
  margin-top: 8px;
}
</style>
